export type TimeOfDay = 'morning' | 'golden' | 'forest' | 'blue-hour';

export type CharacterAction =
  | 'idle'
  | 'look-out'
  | 'run'
  | 'investigate'
  | 'repair'
  | 'comfort'
  | 'group-promise';

export type CharacterExpression =
  | 'curious'
  | 'hopeful'
  | 'joy'
  | 'alert'
  | 'focused'
  | 'kind'
  | 'wonder';

export interface DirectionCue {
  character: 'arlo' | 'pasha' | 'liam' | 'milo' | 'zara' | 'tuk' | 'dad' | 'mom';
  action: CharacterAction;
  expression: CharacterExpression;
  mark: [number, number, number];
  facing: number;
}

export interface CameraCue {
  preset: 'pov' | 'portrait' | 'follow' | 'wide' | 'low' | 'crane' | 'orbit';
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
}

export interface EpisodeShot {
  id: string;
  name: string;
  description: string;
  duration: number;
  camera: CameraCue;
  direction: DirectionCue[];
  note?: string;
}

export interface EpisodeScene {
  id: string;
  number: string;
  name: string;
  location: string;
  timeOfDay: TimeOfDay;
  color: string;
  summary: string;
  storyBeat: string;
  shots: EpisodeShot[];
}

export interface Episode {
  id: string;
  title: string;
  subtitle: string;
  pov: string;
  logline: string;
  scenes: EpisodeScene[];
}

export const WHERE_THE_LAKE_ENDS: Episode = {
  id: 's01e01',
  title: 'Where the Lake Ends',
  subtitle: 'Ranger Pups · Episode 01',
  pov: "Arlo's point of view",
  logline:
    'A young wolf who dreams of the distant city discovers that leadership begins by noticing what the forest—and his pack—need right now.',
  scenes: [
    {
      id: 'mirror',
      number: '01',
      name: 'The Mirror',
      location: 'Wolfe family cabin',
      timeOfDay: 'morning',
      color: '#8fc3d8',
      summary: 'Arlo studies his reflection while the city glimmers beyond Evergreen Lake.',
      storyBeat: 'The dream appears before the choice.',
      shots: [
        {
          id: 'mirror-pov',
          name: 'Paws on the sink',
          description: 'Real POV begins on Arlo’s paws, then tilts into the mirror.',
          duration: 4.5,
          camera: { preset: 'pov', position: [0.4, 2.45, 5.6], target: [0, 2.1, 0], fov: 34 },
          direction: [{ character: 'arlo', action: 'look-out', expression: 'curious', mark: [0, 0, 0], facing: 0 }],
          note: 'Start grounded and tactile; let the fantastic world arrive in the reflection.',
        },
        {
          id: 'mirror-city',
          name: 'World beyond the glass',
          description: 'A gentle orbit reveals the lake, trees, and far city behind Arlo.',
          duration: 5.5,
          camera: { preset: 'orbit', position: [5.8, 3.4, 6.4], target: [0, 1.55, -1.8], fov: 30 },
          direction: [{ character: 'arlo', action: 'look-out', expression: 'hopeful', mark: [0, 0, -0.4], facing: 0.25 }],
        },
      ],
    },
    {
      id: 'breakfast',
      number: '02',
      name: 'The Busy Table',
      location: 'Wolfe family kitchen',
      timeOfDay: 'morning',
      color: '#f2bd75',
      summary: 'A warm, noisy breakfast pulls Arlo back from the horizon.',
      storyBeat: 'Home interrupts the dream—and gives it meaning.',
      shots: [
        {
          id: 'breakfast-wide',
          name: 'Family rhythm',
          description: 'A wide ensemble shot catches Pasha, Liam, Mom, Dad, and Arlo in overlapping motion.',
          duration: 7,
          camera: { preset: 'wide', position: [7.2, 5.2, 8.2], target: [0, 1.2, 0], fov: 31 },
          direction: [
            { character: 'arlo', action: 'look-out', expression: 'hopeful', mark: [-0.8, 0, -0.2], facing: 0 },
            { character: 'pasha', action: 'idle', expression: 'joy', mark: [0.9, 0, 0.4], facing: -0.7 },
            { character: 'liam', action: 'idle', expression: 'joy', mark: [0.2, 0, 1.2], facing: 0.9 },
            { character: 'mom', action: 'idle', expression: 'kind', mark: [-1.5, 0, 0.8], facing: 0.4 },
            { character: 'dad', action: 'idle', expression: 'kind', mark: [1.7, 0, -0.4], facing: -0.4 },
          ],
        },
      ],
    },
    {
      id: 'dock-race',
      number: '03',
      name: 'The Dock Race',
      location: 'Old bridge over Evergreen Lake',
      timeOfDay: 'golden',
      color: '#e58a6a',
      summary: 'Pasha turns the walk to school into a race; Arlo watches, waits, then catches up.',
      storyBeat: 'Arlo leads through timing, not force.',
      shots: [
        {
          id: 'race-low',
          name: 'Boards under paws',
          description: 'Low tracking camera races beside the pups as the lake flashes between dock boards.',
          duration: 6.5,
          camera: { preset: 'low', position: [-5.8, 1.1, 4.3], target: [0.5, 1.05, 0], fov: 38 },
          direction: [
            { character: 'arlo', action: 'run', expression: 'joy', mark: [-0.8, 0, 0], facing: Math.PI / 2 },
            { character: 'pasha', action: 'run', expression: 'joy', mark: [0.9, 0, -0.4], facing: Math.PI / 2 },
            { character: 'liam', action: 'run', expression: 'joy', mark: [-1.8, 0, 0.6], facing: Math.PI / 2 },
          ],
        },
      ],
    },
    {
      id: 'pawprint',
      number: '04',
      name: 'The Big Pawprint',
      location: 'Rain-dark forest trail',
      timeOfDay: 'forest',
      color: '#5f8f77',
      summary: 'The Ranger Pups find an enormous track and hear a branch crack deeper in the trees.',
      storyBeat: 'Curiosity becomes responsibility.',
      shots: [
        {
          id: 'print-overhead',
          name: 'Evidence in the mud',
          description: 'Crane down from the silent canopy into a ring of pups around the track.',
          duration: 5,
          camera: { preset: 'crane', position: [4.8, 10.5, 6], target: [0, 0, 0], fov: 28 },
          direction: [
            { character: 'arlo', action: 'investigate', expression: 'focused', mark: [0, 0, 0.9], facing: Math.PI },
            { character: 'pasha', action: 'investigate', expression: 'alert', mark: [1.3, 0, 0], facing: -Math.PI / 2 },
            { character: 'liam', action: 'investigate', expression: 'wonder', mark: [-1.15, 0, -0.2], facing: Math.PI / 2 },
            { character: 'milo', action: 'investigate', expression: 'focused', mark: [0.6, 0, -1.2], facing: 0 },
          ],
        },
      ],
    },
    {
      id: 'bridge',
      number: '05',
      name: 'One Step at a Time',
      location: 'Rope bridge gorge',
      timeOfDay: 'forest',
      color: '#66a58e',
      summary: 'The pack repairs a failing bridge together, and Arlo tests the path first.',
      storyBeat: 'Leadership is making the path safe for others.',
      shots: [
        {
          id: 'bridge-work',
          name: 'Hands, cord, and timber',
          description: 'A close montage of Zara tying, Milo checking, Pasha holding, and Arlo steadying the line.',
          duration: 7,
          camera: { preset: 'portrait', position: [4.2, 2.9, 5], target: [0, 1.1, 0], fov: 36 },
          direction: [
            { character: 'arlo', action: 'repair', expression: 'focused', mark: [-0.7, 0, 0], facing: 0 },
            { character: 'zara', action: 'repair', expression: 'focused', mark: [0.7, 0, 0.25], facing: -0.5 },
            { character: 'pasha', action: 'repair', expression: 'focused', mark: [1.8, 0, -0.3], facing: -0.6 },
          ],
        },
        {
          id: 'bridge-crossing',
          name: 'Arlo crosses first',
          description: 'A wide side profile lets the gorge and the repaired span carry the drama.',
          duration: 6,
          camera: { preset: 'wide', position: [9, 4.6, 4.5], target: [0, 1.4, 0], fov: 28 },
          direction: [{ character: 'arlo', action: 'run', expression: 'hopeful', mark: [0, 0, 0], facing: Math.PI / 2 }],
        },
      ],
    },
    {
      id: 'forest-dog',
      number: '06',
      name: 'Kind Eyes',
      location: 'Hollow log clearing',
      timeOfDay: 'forest',
      color: '#4e7368',
      summary: 'The supposed monster is an injured old forest dog; Arlo notices fear before danger.',
      storyBeat: 'Observation becomes empathy.',
      shots: [
        {
          id: 'reveal',
          name: 'Lantern reveal',
          description: 'Arlo lowers the lantern and the threatening eye-shine resolves into a hurt animal.',
          duration: 7.5,
          camera: { preset: 'portrait', position: [3.8, 2.2, 4.8], target: [0, 1, 0], fov: 32 },
          direction: [{ character: 'arlo', action: 'comfort', expression: 'kind', mark: [0, 0, 0.5], facing: 0 }],
        },
      ],
    },
    {
      id: 'city-lights',
      number: '07',
      name: 'Carry Home Forward',
      location: 'Hill above the village',
      timeOfDay: 'blue-hour',
      color: '#626a9f',
      summary: 'The pack watches the city lights and promises to cross the water without losing home.',
      storyBeat: 'The horizon is a future the pack will write together.',
      shots: [
        {
          id: 'pack-silhouette',
          name: 'Stars across the lake',
          description: 'The pups form a soft silhouette against the lake and distant city.',
          duration: 8,
          camera: { preset: 'wide', position: [8.5, 5.6, 10.5], target: [0, 1.2, -3.5], fov: 27 },
          direction: [
            { character: 'arlo', action: 'group-promise', expression: 'hopeful', mark: [0, 0, 0], facing: 0 },
            { character: 'pasha', action: 'group-promise', expression: 'joy', mark: [1.15, 0, 0.2], facing: -0.25 },
            { character: 'liam', action: 'group-promise', expression: 'wonder', mark: [-1.05, 0, 0.15], facing: 0.25 },
            { character: 'milo', action: 'group-promise', expression: 'hopeful', mark: [2.2, 0, 0.55], facing: -0.35 },
            { character: 'zara', action: 'group-promise', expression: 'hopeful', mark: [-2.1, 0, 0.55], facing: 0.35 },
            { character: 'tuk', action: 'group-promise', expression: 'wonder', mark: [3.05, 0, 0.95], facing: -0.45 },
          ],
        },
      ],
    },
  ],
};

export const getShot = (sceneId: string, shotId: string): EpisodeShot | undefined =>
  WHERE_THE_LAKE_ENDS.scenes.find((scene) => scene.id === sceneId)?.shots.find((shot) => shot.id === shotId);

export const getScene = (sceneId: string): EpisodeScene | undefined =>
  WHERE_THE_LAKE_ENDS.scenes.find((scene) => scene.id === sceneId);

